import { Box, Flex, Heading, Spinner, Dialog, Text, Button, AlertDialog, Card, Badge, Checkbox, Grid } from '@radix-ui/themes'
import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import UserAvatar from '../../components/common/UserAvatar'
import api from '../../api'
import { STATUS_COLORS } from '../../utils/constants'
import { CheckIcon, Cross2Icon } from '@radix-ui/react-icons'
import { verificationService } from '../../services/verificationService'
import { toast } from 'react-toastify'
import { updateSchoolAdminTeachers } from '../../redux/slices/schoolAdminAccountSlice'

const SchoolVerifications = () => {
    // const accountDetails = useSelector((state) => state.user.accountDetails);
    // const [requests, setRequests] = useState(verifications);
    const [verifications, setVerifications] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState(verifications);
    const [selectedVerification, setSelectedVerification] = useState(null);
    const [view, setView] = useState({ pending: true, approved: false, rejected: false });
    // const [actionType, setActionType] = useState(''); // Tracks if Accept or Decline was selected
    // const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false); // Controls alert dialog visibility
    const [loading, setLoading] = useState({ type: 'page' });
    const dispatch = useDispatch();

    // console.log('verifications', verifications)

    const school = useSelector((state) => state.schoolAdmin.school);
    // console.log('school', school)

    useEffect(() => {
        fetchVerifications();
    }, [])

    const fetchVerifications = async () => {
        setLoading({ type: 'page' });
        try {
            const v = await verificationService.getVerifications(school.id);
            // console.log('verifications fetched:', v);
            setVerifications(v);
        } catch (error) {
            console.error('Error fetching verifications:', error);
            toast.error('Error fetching verifications');
        } finally {
            setLoading(null);
        }
    }

    useEffect(() => {
        setFilteredRequests(verifications.filter(v => view[v.status]));
    }, [view, verifications]);

    const onVerificationChanged = () => {
        fetchVerifications();
    }


    const renderVerificationButtons = (verificationId) => {
        if (selectedVerification.status === 'pending') {
            return (
                <Flex direction="row" gap="2" align="center" justify="center" mt="4" p="4">
                    <Button
                        variant="solid"
                        color="green"
                        onClick={() => { handleApproveVerification(selectedVerification.verification_id) }}>
                        <Spinner loading={loading?.type === 'action' && loading?.id === verificationId}>
                            <CheckIcon />
                        </Spinner>
                        Approve
                    </Button>
                    <Button
                        variant="solid"
                        color="red"
                        onClick={() => handleRejectVerification(selectedVerification.verification_id)}>
                        <Spinner loading={loading?.type === 'action' && loading?.id === verificationId}>
                            <Cross2Icon />
                        </Spinner>
                        Reject
                    </Button>
                </Flex>
            )
        } else if (selectedVerification.status === 'approved') {
            return (
                <Flex direction="row" gap="2" align="center" justify="center" mt="4" p="4">
                    <Button variant="outline" onClick={() => handleRevokeVerification(selectedVerification.verification_id)}>Revoke</Button>
                </Flex>
            )
        }
    }

    const handleVerificationSelect = (verification) => {
        // console.log('verification', verification)
        setSelectedVerification(verification);
    }

    const handleApproveVerification = async () => {
        try {
            setLoading({ type: 'action', id: selectedVerification.id });
            const data = await verificationService.approveVerification(selectedVerification.id);

            dispatch(updateSchoolAdminTeachers(data.school_teachers))

            toast.success('Verification approved successfully');
        } catch (error) {
            console.error('Error approving verification:', error);
            toast.error('Error approving verification');
        } finally {
            setLoading(null);
            onVerificationChanged?.();
            setSelectedVerification(null);
        }
    }

    const handleRejectVerification = async (verificationId) => {
        try {
            setLoading({ type: 'page' });
            const data = await verificationService.rejectVerification(verificationId);
            toast.success('Verification rejected successfully');
            onVerificationChanged?.();
        } catch (error) {
            console.error('Error approving verification:', error);
            toast.error('Error approving verification');
        } finally {
            setLoading(null);
            setSelectedVerification(null);
        }
    }

    const handleRevokeVerification = async (verificationId) => {
        try {
            setLoading({ type: 'action', id: verificationId });
            const data = await verificationService.revokeVerification(verificationId);
            toast.success('Verification revoked successfully');
            setSelectedVerification(null);
            onVerificationChanged?.();
        } catch (error) {
            console.error('Error revoking verification:', error);
            toast.error('Error revoking verification');
        } finally {
            setLoading(null);
        }
    }

    return (
        <Grid columns="6" width="100%" gap="2">
            <Box gridColumnStart={{initial:1, md:1}} gridColumnEnd={{initial:7, md:4}}>
                <Card width="100%" className="p-4" minWidth="500px">
                    <Heading size="2" weight="bold" mr="auto">Verifications</Heading>
                    <Flex direction="row" gap="2" mt="2" align="center" justify="end">
                        <Badge color="cyan"><Text as="label"><Checkbox checked={view.pending} onCheckedChange={() => setView({ ...view, pending: !view.pending })} /> Pending {verifications.filter(v => v.status === 'pending').length}</Text></Badge>
                        <Badge color="green"><Text as="label"><Checkbox checked={view.approved} onCheckedChange={() => setView({ ...view, approved: !view.approved })} /> Approved {verifications.filter(v => v.status === 'approved').length}</Text></Badge>
                        <Badge color="red"><Text as="label"><Checkbox checked={view.blocked} onCheckedChange={() => setView({ ...view, blocked: !view.blocked })} /> Blocked {verifications.filter(v => v.status === 'blocked').length}</Text></Badge>
                    </Flex>
                    <Flex direction="column" gap="2" pt="2">
                        {filteredRequests.map((verification) => (
                            <Flex key={`verification-${verification.verification_id}`} gap="2" align="center" className="cursor-pointer" onClick={() => handleVerificationSelect(verification)}>
                                <UserAvatar user={verification.teacher.user} />
                                <Text size="2">{verification.teacher.user.firstname} {verification.teacher.user.lastname}</Text>
                                <Badge ml="auto" color={STATUS_COLORS[verification.status]}>{verification.status}</Badge>
                            </Flex>
                        ))}
                    </Flex>
                </Card>
            </Box>
        
        <Dialog.Root open={!!selectedVerification} onOpenChange={(open) => open ? null : setSelectedVerification(null)}>
                <Dialog.Content className=" p-4 rounded shadow-lg w-1/2 mx-auto ">
                    {/* <Dialog.Title className="text-lg font-bold">Teacher Details</Dialog.Title> */}
                    {selectedVerification && (
                        <Flex direction="column" gap="2" align="center" justify="center" mt="4" p="4">
                            <UserAvatar size="8" user={selectedVerification.teacher.user} radius="full" />
                            <Text as="p">{selectedVerification.teacher.user.firstname} {selectedVerification.teacher.user.lastname}</Text>
                            <Text as="p">{selectedVerification.teacher.user.email}</Text>
                            <Badge color={STATUS_COLORS[selectedVerification.status]}>{selectedVerification.status}</Badge>
                            <Flex direction="column" gap="2" align="start" mt="4" p="4">
                                <Text as="p" weight="bold" size="2">Registered Courses</Text>
                                <Flex gap="2" className='flex flex-row flex-wrap'>
                                    {selectedVerification.teacher.courses?.length > 0 ? (
                                        selectedVerification.teacher.courses.map((course, i) => (
                                            <Button
                                                key={`${course.subject.id}-${course.level.id}`}
                                                size="1"
                                                variant="soft"
                                                style={{
                                                    border: '2px solid var(--accent-8)',
                                                    backgroundColor: 'var(--accent-5)',
                                                }}
                                            >
                                                <Text size="1" className='flex-1'>
                                                    {course.level.label} | {course.subject.name}
                                                </Text>

                                            </Button>

                                        ))
                                    ) : (
                                        <Text as="p">No courses registered</Text>
                                    )}
                                </Flex>
                            </Flex>
                            {renderVerificationButtons(selectedVerification.verification_id)}
                        </Flex>
                    )}
                    {/* <Dialog.Close asChild>
                        <Button className="mt-4">Close</Button>
                    </Dialog.Close> */}
                </Dialog.Content>
            </Dialog.Root>
        </Grid>
    );
}

export default SchoolVerifications
